import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getIn } from 'formik'

import { keys } from '../../helpers/common'
import { handleScrollToFieldWithError } from '../../components/Form/helpers'

import { setFormStepNext, setFormStepPrevious, setStep } from '../../modules/actions/forms'

import { activeStepSelector } from '../../modules/selectors/forms'

export default function useStepForm({ initialStep, stepCarouselEl, stepFormik, formName }) {
  const dispatch = useDispatch()

  const activeStep = useSelector(activeStepSelector)

  const changeStep = useCallback(newStep => dispatch(setStep(newStep)), [dispatch])

  const nextStep = useCallback(() => dispatch(setFormStepNext()), [dispatch])
  const backStep = useCallback(() => dispatch(setFormStepPrevious()), [dispatch])

  const isStepFieldsValid = useCallback(
    (errors, stepFields) => {
      let isValid = true

      stepFields.forEach(field => {
        keys(errors).forEach(fieldError => {
          if (field === fieldError) {
            isValid = false
            handleScrollToFieldWithError(formName, activeStep)
          }
        })
      })
      return isValid
    },
    [formName, activeStep]
  )

  const stepFormikTouched = stepFormik.touched

  const getNewTouchedValues = useCallback(
    (field, fieldValue) => {
      let touched
      let hasUntouchedFields = false

      if (Array.isArray(fieldValue)) {
        // check each array field and recursively go through the getNewTouchedValues to check touched fields for
        // objects or simple fields
        touched = getIn(stepFormikTouched, field) || []

        fieldValue.forEach((subField, index) => {
          // recursively go through getNewTouchedValues to touch field and it subfields(in case subField is object)
          const newTouchedValue = getNewTouchedValues(`${field}.${index}`, subField)

          if (!!newTouchedValue) {
            hasUntouchedFields = true
            // touch array index element
            touched[index] = newTouchedValue
          }
        })
      } else if (typeof fieldValue === 'object') {
        // check object first level of subfields
        touched = getIn(stepFormikTouched, field) || {}

        keys(fieldValue).forEach(subField => {
          const wasTouched = stepFormikTouched[field] && stepFormikTouched[field][subField]
          if (!wasTouched) {
            hasUntouchedFields = true
            touched[subField] = true
          }
        })
      } else {
        // simple text/number/bool field
        const wasTouched = stepFormikTouched[field]
        if (!wasTouched) {
          hasUntouchedFields = true
          touched = true
        }
      }

      return hasUntouchedFields ? touched : false
    },
    [stepFormikTouched]
  )

  const checkFlatFields = useCallback(
    stepFields => {
      // make sure that all step field are touched/validated
      let hasUntouchedFields = false
      stepFields.forEach(field => {
        if (!stepFormik.touched[field]) {
          hasUntouchedFields = true
        }
      })

      if (hasUntouchedFields) {
        // if field wasn't touched it will not be validated
        // in this case field triggers manually to be touched and run validation
        const updateTouched = {}
        stepFields.forEach(field => (updateTouched[field] = true))

        stepFormik.setTouched(updateTouched, true).then(errors => {
          isStepFieldsValid(errors, stepFields) && nextStep()
        })
      } else {
        isStepFieldsValid(stepFormik.errors, stepFields) && nextStep()
      }
    },
    [isStepFieldsValid, nextStep, stepFormik]
  )

  const checkIncludedFields = useCallback(
    (stepFields, values) => {
      // make sure that all step field are touched/validated
      let hasUntouchedFields = false
      const updateTouched = {}

      stepFields.forEach(field => {
        const newTouchedValue = getNewTouchedValues(field, values[field])

        if (!!newTouchedValue) {
          hasUntouchedFields = true
          updateTouched[field] = newTouchedValue
        }
      })

      if (hasUntouchedFields) {
        // if field wasn't touched it will not be validated
        // in this case field triggers manually to be touched and run validation
        stepFormik.setTouched(updateTouched, true).then(errors => {
          isStepFieldsValid(errors, stepFields) && nextStep()
        })
      } else {
        isStepFieldsValid(stepFormik.errors, stepFields) && nextStep()
      }
    },
    [isStepFieldsValid, nextStep, stepFormik, getNewTouchedValues]
  )

  const handleStepChange = useCallback(
    (stepFields, values) => {
      if (values) {
        // check nested fields i.e. arrays and objects
        // the function is simple and check only first level
        checkIncludedFields(stepFields, values)
      } else if (stepFields && stepFields.length) {
        checkFlatFields(stepFields)
      } else {
        nextStep()
      }
    },
    [nextStep, checkFlatFields, checkIncludedFields]
  )

  const resetSteps = useCallback(() => {
    changeStep(initialStep || 0)
  }, [changeStep, initialStep])

  useEffect(() => {
    // scroll to top on every step change
    // look for the scroll container through the data-content attribute
    const carouselContainer = stepCarouselEl?.current?.closest('div[data-content]')
    carouselContainer &&
      carouselContainer.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
  }, [activeStep, stepCarouselEl])

  useEffect(() => {
    if (initialStep) {
      // set initialStep on StepForm render
      dispatch(setStep(initialStep))
    }

    return () => {
      // reset step to 0 only on unMount - to avoid reseting StepForm on Route selection!
      dispatch(setStep(0))
    }
  }, [initialStep, dispatch])

  return {
    changeStep,
    backStep,
    resetSteps,
    handleStepChange,
    isStepFieldsValid
  }
}
